import type { components } from "@/types/api.generated";
import apiClient from "./client";

/**
 * Gerar a análise do plano de contas (#1374): o motor de aderência (P5 e P9) e a
 * importação da planilha da análise de referência (P0).
 *
 * Aliases sobre os tipos gerados do OpenAPI: o contrato mora no backend
 * (`routers/coa_validation.py`).
 */
export type CoaEngineRun = components["schemas"]["CoaEngineRunStatus"];
export type CoaValidationSummary = components["schemas"]["CoaValidationSummary"];

/** `POST .../coa-validation/run`: dispara o motor em segundo plano (202). Já rodando, 409. */
export async function runCoaEngine(projectId: string): Promise<CoaEngineRun> {
  const res = await apiClient.post<CoaEngineRun>(
    `/api/projects/${projectId}/coa-validation/run`,
    {},
  );
  return res.data;
}

/** `GET .../coa-validation/run`: `idle`, `running`, `done` ou `failed` com o motivo. */
export async function getCoaEngineRun(projectId: string): Promise<CoaEngineRun> {
  const res = await apiClient.get<CoaEngineRun>(`/api/projects/${projectId}/coa-validation/run`);
  return res.data;
}

/**
 * `POST .../coa-validation/import`: a planilha no layout "Analise De-Para" vira a
 * análise vigente. Planilha fora do layout é 422 com o motivo; acima de 10 MB, 413.
 */
export async function importCoaAnalysis(
  projectId: string,
  file: File,
): Promise<CoaValidationSummary> {
  const form = new FormData();
  form.append("file", file);
  // Sem o cabeçalho, vale o `application/json` padrão do cliente, e o axios
  // transforma o FormData em JSON: o campo `file` some e a resposta é 422.
  const res = await apiClient.post<CoaValidationSummary>(
    `/api/projects/${projectId}/coa-validation/import`,
    form,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
  );
  return res.data;
}

/** Revisão em massa no Excel (#1374, P8). */
export type CoaWorkbookPreview = components["schemas"]["WorkbookPreviewOut"];
export type CoaWorkbookApply = components["schemas"]["WorkbookApplyOut"];

/** O filtro do detalhe: a planilha sai com as contas que a tela mostra. */
export interface CoaWorkbookFilters {
  status?: string;
  decision?: string;
  q?: string;
}

function attachmentName(header: unknown, fallback: string): string {
  const value = typeof header === "string" ? header : "";
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(value);
  if (encoded) return decodeURIComponent(encoded[1]);
  const plain = /filename="?([^";]+)"?/i.exec(value);
  return plain ? plain[1] : fallback;
}

/**
 * `GET .../coa-validation/workbook`: baixa a planilha de curadoria (todas as
 * contas ou o filtro). O backend registra o download e põe o id dele na aba de
 * controle, para a carga saber de qual download veio.
 */
export async function downloadCoaWorkbook(
  projectId: string,
  filters: CoaWorkbookFilters = {},
): Promise<void> {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value != null && value !== ""),
  );
  const res = await apiClient.get<Blob>(`/api/projects/${projectId}/coa-validation/workbook`, {
    params,
    responseType: "blob",
    timeout: 120_000,
  });
  const name = attachmentName(res.headers?.["content-disposition"], "curadoria-plano-de-contas.xlsx");
  const url = URL.createObjectURL(new Blob([res.data]));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function workbookForm(file: File, extra: Record<string, string> = {}): FormData {
  const form = new FormData();
  form.append("file", file);
  for (const [key, value] of Object.entries(extra)) form.append(key, value);
  return form;
}

/** `POST .../workbook/preview`: o que a carga mudaria, sem gravar. Planilha recusada inteira é 422. */
export async function previewCoaWorkbook(projectId: string, file: File): Promise<CoaWorkbookPreview> {
  const res = await apiClient.post<CoaWorkbookPreview>(
    `/api/projects/${projectId}/coa-validation/workbook/preview`,
    workbookForm(file),
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
  );
  return res.data;
}

/**
 * `POST .../workbook/apply`: grava as alteradas e os conflitos em que o
 * consultor escolheu a planilha (`{"conta": "file"}`). Conta que mudou de novo
 * desde a prévia dá 409 e nada é gravado.
 */
export async function applyCoaWorkbook(
  projectId: string,
  file: File,
  conflictChoices: Record<string, "file">,
): Promise<CoaWorkbookApply> {
  const res = await apiClient.post<CoaWorkbookApply>(
    `/api/projects/${projectId}/coa-validation/workbook/apply`,
    workbookForm(file, { conflict_choices: JSON.stringify(conflictChoices) }),
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
  );
  return res.data;
}

export type YcoaWithoutClient = components["schemas"]["YcoaWithoutClient"];
export type YcoaWithoutClientAccount = components["schemas"]["YcoaWithoutClientAccount"];

/**
 * `GET .../coa-validation/ycoa-without-client`: as contas do YCOA que nenhuma conta
 * do cliente recebe (#1715, cartão 7.2), sem decisão. `null` sem análise.
 */
export async function getYcoaWithoutClient(projectId: string): Promise<YcoaWithoutClient | null> {
  const res = await apiClient.get<YcoaWithoutClient | null>(
    `/api/projects/${projectId}/coa-validation/ycoa-without-client`,
  );
  return res.data;
}
